import { useState, useEffect, useMemo } from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import apiClient from '../../services/api/apiClient';
import { Helmet } from 'react-helmet-async';
import { TJournalisation } from '../../utils/types/TJournalisation';
import JournalisationGrid from '../../components/grid/JournalisationGrid';
import { Spinner } from 'react-bootstrap';
import JournalisationTypeOperationSelect from '../../components/select/JournalisationTypeOperationSelect';
import { useQuery } from '@tanstack/react-query';
import QueryErrorAlerts, { QueryErrorEntry } from '../../components/others/QueryErrorAlerts';

const JournalisationPage = () => {
    const [typeOperation, setTypeOperation] = useState<string | null>(null);
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string | null>(null);

    // ---

    const {
        data: serverDate = null,
        refetch: refetchServerDate,
        isError: isServerDateError,
        error: serverDateError,
    } = useQuery<string>({
        queryKey: ['serverDate'],
        queryFn: async () => {
            const res = await apiClient.get(`/api/data/serverDt`);
            return res.data;
        },
        meta: { title: 'Chargement de la date serveur' },
        retry: 3,
        staleTime: 0,
    });

    const {
        data: journalisations = [],
        isLoading: isLoadingJournalisations,
        refetch: refetchJournalisations,
        isError: isJournalisationsError,
        error: journalisationsError,
    } = useQuery<TJournalisation[]>({
        queryKey: [
            'journalisations',
            {
                typeOperation: typeOperation,
                dateDebut: startDate,
                dateFin: endDate,
                searchTerm: searchTerm,
            },
        ],
        queryFn: async () => {
            const res = await apiClient.get('/api/journalisation/get-data-terminal', {
                params: {
                    typeOperation: typeOperation,
                    dateDebut: startDate,
                    dateFin: endDate,
                    searchTerm: searchTerm,
                },
            });
            return res.data ?? [];
        },
        meta: { title: 'Chargement des logs' },
        enabled: false,
    });

    // ---

    useEffect(() => {
        if (!serverDate) return;

        const serverDt = new Date(serverDate);

        const localYear = serverDt.getFullYear();
        const localMonth = serverDt.getMonth() + 1;
        const localDate = serverDt.getDate();

        const pad = (n: any) => n.toString().padStart(2, '0');

        const startDateString = `${localYear}-${pad(localMonth)}-01`;
        const endDateString = `${localYear}-${pad(localMonth)}-${pad(localDate)}`;

        setStartDate(startDateString);
        setEndDate(endDateString);
    }, [serverDate]);

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isServerDateError, error: serverDateError, title: 'Erreur lors du chargement de la date serveur', refetch: refetchServerDate },
            { isError: isJournalisationsError, error: journalisationsError, title: 'Erreur lors du chargement des journalisations', refetch: refetchJournalisations },
        ],
        [isServerDateError, serverDateError, refetchServerDate, isJournalisationsError, journalisationsError, refetchJournalisations],
    );

    // ---

    return (
        <>
            <Helmet>
                <title>Journalisations :: Portail PETROSIL</title>
            </Helmet>

            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <div className="page-pretitle">Listing</div>
                            <h2 className="page-title">Journalisations</h2>
                        </div>
                        <div className="col-auto ms-auto d-print-none">
                            <div className="btn-list">
                                <span className="d-none d-sm-inline">
                                    <button
                                        type="button"
                                        className="btn btn-1"
                                        onClick={() => {
                                            refetchJournalisations();
                                        }}
                                    >
                                        {isLoadingJournalisations ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                &nbsp; Générer
                                            </>
                                        ) : (
                                            'Générer'
                                        )}
                                    </button>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    <QueryErrorAlerts queries={queryErrors} />

                    <div className="card">
                        <div className="accordion accordion-flush" id="accordion-flush">
                            <div className="accordion-item">
                                <button className="accordion-header" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-1-flush" aria-expanded="true">
                                    <div className="accordion-header-text">
                                        <h4>Filtre</h4>
                                    </div>
                                    <div className="accordion-header-toggle">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-1">
                                            <path d="M6 9l6 6l6 -6"></path>
                                        </svg>
                                    </div>
                                </button>
                                <div id="collapse-1-flush" className="accordion-collapse collapse show" data-bs-parent="#accordion-flush">
                                    <div className="accordion-body">
                                        <div>
                                            <div className="row">
                                                <div className="col-sm-2 form-group">
                                                    <label htmlFor="DateFin">Type opération</label>
                                                    <JournalisationTypeOperationSelect setTypeOperation={setTypeOperation} selectedTypeOperation={typeOperation} />
                                                </div>

                                                <div className="col-sm-2 form-group">
                                                    <label htmlFor="DateDebut">Du</label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={startDate ?? ''}
                                                        onChange={(e) => {
                                                            setStartDate(e.target.value);
                                                        }}
                                                    />
                                                </div>

                                                <div className="col-sm-2 form-group">
                                                    <label htmlFor="DateFin">Au</label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={endDate ?? ''}
                                                        onChange={(e) => {
                                                            setEndDate(e.target.value);
                                                        }}
                                                    />
                                                </div>

                                                <div className="col-sm-2 form-group">
                                                    <label htmlFor="DateFin">Terme de recherche</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={searchTerm ?? ''}
                                                        onChange={(e) => {
                                                            setSearchTerm(e.target.value);
                                                        }}
                                                        placeholder=""
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-group mt-2">
                                                <button type="button" className="btn btn-outline-secondary" disabled={isLoadingJournalisations}>
                                                    {isLoadingJournalisations ? (
                                                        <>
                                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                            &nbsp; Générer
                                                        </>
                                                    ) : (
                                                        'Générer'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-2">
                        <JournalisationGrid journalisations={journalisations} />
                    </div>
                </div>
            </div>
        </>
    );
};

JournalisationPage.Layout = DefaultLayout;

export default JournalisationPage;
